import { useEffect, useRef, useState } from 'react';
import { flagExercise } from '../../api/client';

interface Props {
  exerciseId: string;
  disabled?: boolean;
}

const REASONS: { id: string; label: string; hint: string }[] = [
  { id: 'falsch', label: 'Antwort ist falsch', hint: 'Die „richtige" Antwort stimmt nicht.' },
  { id: 'verwirrend', label: 'Frage ist verwirrend', hint: 'Unklar formuliert oder doppeldeutig.' },
  { id: 'doppelt', label: 'Schon mal gesehen', hint: 'Die Frage oder Antwort wiederholt sich zu oft.' },
];

const FOCUSABLE = 'button:not([disabled]), [href], textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function FlagQuestionButton({ exerciseId, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const firstReasonRef = useRef<HTMLButtonElement | null>(null);

  async function submit(rawReason: string) {
    const combined = note.trim() ? `${rawReason}: ${note.trim()}` : rawReason;
    setStatus('sending');
    try {
      await flagExercise({ exercise_id: exerciseId, reason: combined });
      setStatus('done');
      setTimeout(() => {
        setOpen(false);
        setStatus('idle');
        setReason('');
        setNote('');
      }, 1400);
    } catch {
      setStatus('error');
    }
  }

  // Esc-close, focus trap, autoFocus, restore focus on close.
  useEffect(() => {
    if (!open) return;

    // autoFocus: first reason button after paint.
    const id = window.setTimeout(() => {
      firstReasonRef.current?.focus();
    }, 0);

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => { if (!disabled) setOpen(true); }}
        disabled={disabled}
        aria-label="Diese Frage melden"
        title="Diese Frage melden"
        style={{
          minWidth: 44, minHeight: 44,
          padding: '6px 12px',
          fontSize: 11,
          fontFamily: 'var(--mono)',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--ink-muted)',
          background: 'transparent',
          border: '1px solid rgba(60,50,40,0.18)',
          borderRadius: 3,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.4 : 1,
        }}
      >🚩 Frage melden</button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="flag-dialog-title"
          aria-describedby="flag-dialog-desc"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(30,24,18,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, animation: 'fade-in-up 0.2s ease-out',
          }}
        >
          <div
            ref={dialogRef}
            style={{
              background: 'var(--parchment)', padding: 28, borderRadius: 6,
              width: 'min(460px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              fontFamily: 'var(--serif)',
            }}
          >
            <div id="flag-dialog-title" style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
              Was stimmt nicht?
            </div>
            <div id="flag-dialog-desc" style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 18 }}>
              Deine Meldung hilft uns, Finns Fragen besser zu machen. 🦊
            </div>

            {status !== 'done' ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {REASONS.map((r, i) => (
                    <button
                      key={r.id}
                      ref={i === 0 ? firstReasonRef : undefined}
                      onClick={() => { setReason(r.id); submit(r.id); }}
                      disabled={status === 'sending'}
                      style={{
                        textAlign: 'left', padding: '12px 14px',
                        border: `1px solid ${reason === r.id ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                        borderRadius: 3, background: 'var(--card)',
                        cursor: status === 'sending' ? 'default' : 'pointer',
                        fontFamily: 'var(--sans)',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{r.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 2 }}>{r.hint}</div>
                    </button>
                  ))}
                </div>

                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-muted)' }}>
                    Optional: kurze Notiz
                  </label>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value.slice(0, 200))}
                    disabled={status === 'sending'}
                    rows={2}
                    placeholder="z.B. Marienkäfer hat 5 Silben, nicht 4"
                    style={{
                      width: '100%', marginTop: 4, padding: 8,
                      fontFamily: 'var(--serif)', fontSize: 13,
                      border: '1px solid rgba(60,50,40,0.18)', borderRadius: 3,
                      resize: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      minHeight: 44,
                      padding: '10px 18px', background: 'transparent',
                      border: '1px solid rgba(60,50,40,0.18)', borderRadius: 3,
                      fontFamily: 'var(--sans)', fontSize: 13, cursor: 'pointer',
                    }}
                  >Abbrechen</button>
                </div>

                {status === 'error' && (
                  <div role="alert" style={{ marginTop: 12, color: 'var(--wrong)', fontSize: 13 }}>
                    Meldung konnte nicht gesendet werden. Bitte nochmal versuchen.
                  </div>
                )}
              </>
            ) : (
              <div role="status" aria-live="polite" style={{ padding: '14px 0', fontFamily: 'var(--display)', fontSize: 18 }}>
                Danke, das hilft uns besser zu werden 🦊
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
